import {
  fetchProjectChats,
  fetchProjectMeetings,
  useSDK,
  useStores,
  type SharedContentItem,
} from '@littlebird/sdk';
import { mergeSharedContent } from '@littlebird/ui-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { logError } from '@/lib/errorLogging';
import { queryClient } from '@/query/client';
import { userStore } from '@/stores/user.store';

type Kind = 'chat' | 'meeting';

const projectChatsQueryKey = (projectId: string) =>
  ['project-chats', projectId] as const;

const projectMeetingsQueryKey = (projectId: string) =>
  ['project-meetings', projectId] as const;

export const invalidateProjectChats = (projectId: string) => {
  queryClient.invalidateQueries({ queryKey: projectChatsQueryKey(projectId) });
};

export const invalidateProjectMeetings = (projectId: string) => {
  queryClient.invalidateQueries({
    queryKey: projectMeetingsQueryKey(projectId),
  });
};

const SOURCES = {
  chat: {
    fetchPage: fetchProjectChats,
    queryKey: projectChatsQueryKey,
    loadOwnItems: (stores: ReturnType<typeof useStores>, projectId: string) =>
      stores.threads.loadProjectThreads(projectId),
    ownItems: (
      projects: ReturnType<typeof useStores>['projects'],
      projectId: string,
      authorUserId: number
    ): SharedContentItem[] =>
      projects
        .getThreads(projectId)
        .filter((thread) => thread.synced)
        .map((thread) => ({
          type: 'chat',
          id: thread.id,
          title: thread.title,
          preview: null,
          updatedAt: thread.updatedAt.toISOString(),
          authorUserId,
          authorEmail: userStore.user?.email ?? null,
          authorName: userStore.user?.name ?? null,
        })),
    localProjectId: (
      stores: ReturnType<typeof useStores>,
      id: string
    ): string | null | undefined => stores.threads.getById(id)?.projectId,
  },
  meeting: {
    fetchPage: fetchProjectMeetings,
    queryKey: projectMeetingsQueryKey,
    loadOwnItems: (stores: ReturnType<typeof useStores>, projectId: string) =>
      stores.meetings.loadProjectMeetings(projectId),
    ownItems: (
      projects: ReturnType<typeof useStores>['projects'],
      projectId: string,
      authorUserId: number
    ): SharedContentItem[] =>
      projects.getMeetings(projectId).map((meeting) => ({
        type: 'meeting',
        id: meeting.id,
        title: meeting.name || 'Untitled meeting',
        preview: null,
        updatedAt: meeting.updatedAt.toISOString(),
        authorUserId,
        authorEmail: userStore.user?.email ?? null,
        authorName: userStore.user?.name ?? null,
      })),
    localProjectId: (
      stores: ReturnType<typeof useStores>,
      id: string
    ): string | null | undefined => stores.meetings.get(id)?.projectId,
  },
} as const;

export function useProjectSharedContent(kind: Kind, projectId: string) {
  // Project and archive state are MobX observables, not React dependencies.
  'use no memo';
  const source = SOURCES[kind];
  const sdk = useSDK();
  const stores = useStores();
  const currentUserId = userStore.userId ? Number(userStore.userId) : null;

  const query = useInfiniteQuery({
    queryKey: source.queryKey(projectId),
    queryFn: ({ pageParam }) => source.fetchPage(sdk, projectId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? null,
    refetchOnWindowFocus: true,
  });

  // Project-scoped, not `ensureListLoadedFromStorage()`: that loads only the first page
  // of the global list, dropping the author's own older rows in this project.
  useEffect(() => {
    source.loadOwnItems(stores, projectId).catch((err) => {
      logError(err, `Failed to load project ${kind}s from local storage`, {
        context: 'useProjectSharedContent:loadOwnItems',
        metadata: { projectId },
      });
    });
  }, [kind, projectId, source, stores]);

  useEffect(() => {
    if (query.isError) {
      logError(query.error, `Failed to load project ${kind}s`, {
        context: 'useProjectSharedContent',
        metadata: { projectId, kind },
      });
    }
  }, [kind, projectId, query.error, query.isError]);

  const remoteItems = query.data?.pages.flatMap((page) => page.items) ?? [];
  const remotePreviews = new Map(
    remoteItems.map((item) => [item.id, item.preview])
  );
  const ownItems = source
    .ownItems(stores.projects, projectId, currentUserId ?? -1)
    .map((item) => ({ ...item, preview: remotePreviews.get(item.id) ?? null }));

  const data = mergeSharedContent({
    ownItems,
    remoteItems,
    projectId,
    localProjectId: (id) => source.localProjectId(stores, id),
  }).filter(
    (item) => item.type !== 'chat' || !stores.threads.getById(item.id)?.archived
  );

  return {
    data,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
  };
}
